import { useContext, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  selectLensEditorLensId,
  selectProfileBuilderCellsByFieldId,
  selectProfileBuilderFieldById,
  updateCell,
} from "redux/slices/lensEditor";
import { Box } from "@mui/material";
import NewCellDialog from "./NewCellDialog";
import CellEditor from "./CellEditor";
import { useParamNumbers } from "helpers/hooks/useParamNumbers";
import { Navigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { LensUpdateSnackbarContext } from "contexts/LensUpdateSnackbarContext";
import { CellFormState } from "./types";

const ProfileBuilderCell = () => {
  // Move to ID, so the state gets refreshed automatically
  const { fieldId, cellId } = useParamNumbers(["fieldId", "cellId"]);
  const fieldCells = useAppSelector((state) => selectProfileBuilderCellsByFieldId(state, fieldId));
  const selectedCell = fieldCells.find((c) => c.id === cellId);
  const [showNewCellDialog, setShowNewCellDialog] = useState<boolean>(false);
  const selectedField = useAppSelector((state) => selectProfileBuilderFieldById(state, fieldId));
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const lensId = useAppSelector(selectLensEditorLensId);
  const { enqueueLensUpdateTimerSnackbar } = useContext(LensUpdateSnackbarContext);

  const handleUpdateCell = (cell: CellFormState) => {
    if (selectedCell) {
      dispatch(
        updateCell({
          id: selectedCell.id,
          values: {
            default: cell.default,
            exportExclude: cell.exportExclude,
            key: cell.key,
            name: cell.name,
            optional: cell.optional,
            rank: cell.rank,
            selectOptions: cell.selectOptions,
            type: cell.type,
            description: cell.description,
            analyticsExclude: cell.analyticsExclude,
            validationConfig: cell.validationConfig,
          },
          clears: {
            key: cell.key ? undefined : true,
            default: cell.default ? undefined : true,
          },
        })
      )
        .unwrap()
        .then(() => {
          enqueueSnackbar("Cell updated", {
            variant: "success",
          });
          enqueueLensUpdateTimerSnackbar(lensId);
        })
        .catch((gqlError) => {
          enqueueSnackbar(`Unable to update cell - ${gqlError?.message}`, {
            variant: "error",
          });
        });
    }
  };

  if (!selectedCell || !selectedField) return <Navigate to=".." />;

  return (
    <>
      {showNewCellDialog && selectedField && (
        <NewCellDialog
          open={showNewCellDialog}
          close={() => setShowNewCellDialog(false)}
          field={selectedField}
          rank={selectedField.cells.length}
        />
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          "& .MuiTextField-root,.MuiFormControl-root,.MuiFormControlLabel-root": { m: 1 },
        }}>
        {selectedCell && selectedField && (
          <CellEditor
            cell={selectedCell as CellFormState}
            field={selectedField}
            rank={selectedCell.rank}
            onSave={handleUpdateCell}
          />
        )}
      </Box>
    </>
  );
};

export default ProfileBuilderCell;
